import { Button, Icon } from '@octano/global-ui';
import { CellFormatOptions } from '@octano/global-ui/dist/components/Table/types/TableTypes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';

import { TranslationsKeys } from '../../../../locales/translations';
import { StudyPlanListItem, StudyPlanVersionStatus } from '../api';

type SelectOptions = {
  value: StudyPlanVersionStatus;
  label: string;
};

enum STATUS_COLOR {
  Borrador = 'danger',
  Activo = 'success',
  Vigente = 'primary',
  Cerrado = 'danger',
}

enum STATUS_ICON {
  Borrador = 'warning',
  Activo = 'success',
  Vigente = 'success',
  Cerrado = 'error',
}

const IconOption = (props: { data: SelectOptions }) => (
  <div className="d-flex align-items-center" style={{ gap: '10px' }}>
    <Icon
      className="me-2"
      color={STATUS_COLOR[props.data.value]}
      name={STATUS_ICON[props.data.value]}
      size="15px"
    />
    <span>{props.data.label}</span>
  </div>
);

interface UseStudyPlansColumnsProps {
  onStatusChange: (
    versionId: number,
    newStatus: StudyPlanVersionStatus,
  ) => void;
  onManage: (id: number, type: string | undefined) => void;
}

export function useStudyPlansColumns({
  onStatusChange,
  onManage,
}: UseStudyPlansColumnsProps) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'list';

  const statusOptions: SelectOptions[] = useMemo(
    () => [
      { value: 'Borrador', label: t(`${prefix}.status.Borrador`) },
      { value: 'Activo', label: t(`${prefix}.status.Activo`) },
      { value: 'Vigente', label: t(`${prefix}.status.Vigente`) },
      { value: 'Cerrado', label: t(`${prefix}.status.Cerrado`) },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      {
        columnName: 'name',
        headerText: t(`${prefix}.columns.name`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '20%',
      },
      {
        columnName: 'code',
        headerText: t(`${prefix}.columns.code`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '10%',
      },
      {
        columnName: 'modality',
        headerText: t(`${prefix}.columns.modality`),
        thClassName: 'text-center text-capitalize',
        tdClassName: 'text-center',
        width: '12%',
        cellFormat: (options: CellFormatOptions) => options.value?.name || '-',
      },
      {
        columnName: 'school',
        headerText: t(`${prefix}.columns.school`),
        thClassName: 'text-center text-capitalize',
        tdClassName: 'text-center',
        width: '12%',
        cellFormat: (options: CellFormatOptions) => options.value?.name || '-',
      },
      {
        columnName: 'studyPlanCe',
        headerText: t(`${prefix}.columns.certificate`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: '10%',
        cellFormat: (options: CellFormatOptions) => {
          const hasCertificate = options.value?.hasCertificate;
          return (
            <b>{hasCertificate ? t(`${prefix}.yes`) : t(`${prefix}.no`)}</b>
          );
        },
      },
      {
        columnName: 'versions',
        headerText: t(`${prefix}.columns.status`),
        thClassName: 'text-center',
        width: '15%',
        cellFormat: (options: CellFormatOptions) => {
          const versions = options.value as StudyPlanListItem['versions'];
          const version = versions?.[0];
          if (!version) {
            return '-';
          }

          const currentStatus = version.status;
          const currentOption = statusOptions.find(
            (opt) => opt.value === currentStatus,
          );

          return (
            <div className="d-flex align-items-center justify-content-center">
              <Select
                options={statusOptions}
                styles={{
                  indicatorSeparator: () => ({}),
                  container: (styles) => ({
                    ...styles,
                    width: '100%',
                  }),
                  valueContainer: (styles) => ({
                    ...styles,
                  }),
                  menu: (styles) => ({
                    ...styles,
                    zIndex: 1000,
                  }),
                }}
                className="react-select"
                classNamePrefix="react-select"
                value={currentOption}
                components={{ SingleValue: IconOption }}
                isSearchable={false}
                onChange={(current) => {
                  if (current && current.value !== currentStatus) {
                    onStatusChange(version.id, current.value);
                  }
                }}
              />
            </div>
          );
        },
      },
      {
        columnName: 'id',
        headerText: t(`${prefix}.columns.action`),
        thClassName: 'text-center',
        width: '120px',
        cellFormat: (options: CellFormatOptions) => {
          const row = options.row as StudyPlanListItem;
          const type = row.studyPlanCe?.type;
          const buttonText = row.isInActiveOffer
            ? t(`${prefix}.view`)
            : t(`${prefix}.manage`);

          return (
            <Button
              type="button"
              text={buttonText}
              size="sm"
              outlined
              fullwidth
              color="primary"
              onClick={() => onManage(options.value, type)}
            />
          );
        },
      },
    ],
    [t, statusOptions, onStatusChange, onManage],
  );

  return columns;
}
